import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation, getI18n } from 'react-i18next';

import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import Typography from '@material-ui/core/Typography';

import MaterialTable from 'components/Table/Table';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import Tooltip from 'components/Tooltip/Tooltip';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Info from 'components/Tooltip/Info';
import { isoDateTimeToLabel } from 'helpers/helpers';

import { UIStore, ServiceStatus } from 'stores/uiStore';
import { EdwardColors } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    iconLink: {
      color: EdwardColors.DARK,
      display: 'flex',
      '& a': {
        padding: theme.spacing(2, 2),
        display: 'inline-flex',
        color: EdwardColors.DARK,
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing('13px', 1),
        },
        '@media (max-width: 400px)': {
          padding: theme.spacing(2, 1),
        },
      },
    },
    list: {
      marginBottom: theme.spacing(4),
    }
  }),
);

interface Props {
  uiStore?: UIStore;
}

const Services: React.FC<Props> = ({ uiStore }) => {
  const { t } = useTranslation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const loading = uiStore?.serviceStatus.length === 0;
  const warning = uiStore?.serviceStatus.some((service) => service.status !== ServiceStatus.OK);

  useEffect(() => {
    if (servicesOpen) {
      uiStore!.getServicesHistory();
    }
  }, [servicesOpen, uiStore]);

  const classes = useStyles();
  return (
    <>
      <div className={`${classes.iconLink}`} onClick={() => setServicesOpen(true)}>
        <Tooltip title={t('services_title') || ''}>
          {loading ? (
            <LoadingIndicator />
          ) : warning ? (
            <WarningIcon htmlColor={EdwardColors.ALERT} />
          ) : (
            <CheckCircleOutlineIcon htmlColor={EdwardColors.GRAY_MEDIUM} />
          )}
        </Tooltip>
      </div>

      <ConfirmDialog
        isOpen={servicesOpen}
        confirm={() => setServicesOpen(false)}
        title={t('services_title')}
        maxWidth="sm"
      >
        <table className={classes.list}>
          <tbody>
            {uiStore?.serviceStatus.map((service) => (
              <tr key={service.id}>
                <td>
                  {service.status === ServiceStatus.DOWN ? (
                    <WarningIcon htmlColor={EdwardColors.ALERT} />
                  ) : (
                    <CheckCircleIcon htmlColor={EdwardColors.GREEN} />
                  )}
                </td>
                <td>{t(`service_${service.id}`)}</td>
                <td>
                  {service.status === ServiceStatus.DOWN && getI18n().exists(`service_${service.id}_DOWN`) && (
                    <Info content={t(`service_${service.id}_DOWN`)} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Typography variant="h2">{t('services_history')}</Typography>

          <MaterialTable
            options={{
              header: true,
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              emptyRowsWhenPaging: false,

              pageSizeOptions: [5,10,20],
              paging: true,
              pageSize: 5,

              //paging: false
            }}

            data={uiStore!.serviceHistory}
            columns={[
              {
                title: t('service'),
                field: 'service',
              },
              {
                title: t('general_Since'),
                field: 'since',
                defaultSort: 'desc',
                render: ({ since }) => isoDateTimeToLabel(since),
                cellStyle: {
                  whiteSpace: 'nowrap',
                },
              },
              {
                title: t('general_Until'),
                field: 'until',
                render: ({ until }) => isoDateTimeToLabel(until),
                cellStyle: {
                  whiteSpace: 'nowrap',
                },
              },
            ]}
          />

      </ConfirmDialog>
    </>
  );
};

export default inject('uiStore')(observer(Services));
